import React, { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { startCase } from "lodash";
import AuthContext from "context/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";

const VideoView = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { updatePageName } = useContext(AuthContext);
  const navigate = useNavigate();
  
  useEffect(() => {
    updatePageName(t("VIEW_VIDEO"));
  }, []);

  const video = location?.state;

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    
    // Extract video ID from various YouTube URL formats
    const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
    const match = url.match(regExp);
    const videoId = (match && match[7].length === 11) ? match[7] : null;
    
    return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
  };

  const renderVideoPlayer = () => {
    if (video?.videoType === 'youtube' && video?.videoLink) {
      const embedUrl = getYouTubeEmbedUrl(video.videoLink);
      if (embedUrl) {
        return (
          <div className="w-full">
            <h3 className="text-lg font-semibold mb-3 text-gray-800">{t('VIDEO')}</h3>
            <iframe
              src={embedUrl}
              title={video.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-64 rounded-lg"
            ></iframe>
          </div>
        );
      }
      return <div className="text-red-500">Invalid YouTube URL</div>;
    } else if (video?.videoType === 'upload' && video?.videoFile) {
      return (
        <div className="w-full">
          <h3 className="text-lg font-semibold mb-3 text-gray-800">{t('VIDEO')}</h3>
          <video controls className="w-full h-64 rounded-lg object-contain bg-black">
            <source src={video.videoFile} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
      );
    }
    return <div className="text-gray-500">No video available</div>;
  };

  const renderThumbnail = () => {
    if (video?.videoType === 'upload' && video?.thumbnail) {
      return (
        <div className="w-full">
          <h3 className="text-lg font-semibold mb-3 text-gray-800">{t('THUMBNAIL')}</h3>
          <div className="flex justify-center">
            <img 
              src={video.thumbnail} 
              alt={`${video.title} thumbnail`}
              className="w-full h-64 rounded-lg object-contain"
            />
          </div>
        </div>
      );
    }
    return null;
  };

  const getVideoTypeBadge = (type) => {
    const colors = {
      youtube: 'bg-red-100 text-red-800',
      upload: 'bg-blue-100 text-blue-800'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-sm ${colors[type]}`}>
        {type === 'youtube' ? 'YouTube' : 'Uploaded Video'}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-red-100 text-red-800'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-sm ${colors[status]}`}>
        {startCase(status)}
      </span>
    );
  };

  const hasThumbnail = video?.videoType === 'upload' && video?.thumbnail;

  return (
    <>
      <div>
        <div className="bg-[#F9F9F9]">
          <div className="px-3 py-4">
            <div className="bg-white border border-[#E9EDF9] rounded-lg">
              <div className=" border-b-[#E3E3E3] grid 2xl:grid-cols-3 xl:grid-cols-2 lg:grid lg:grid-cols-1 gap-2 px-4 ">
                <div className="col-span-2 flex flex-wrap  items-center" />
              </div>
              <div className="relative p-6 flex-auto">
                {/* Video and Thumbnail Section */}
                <div className={`mb-8 ${hasThumbnail ? 'grid grid-cols-1 lg:grid-cols-2 gap-6' : 'flex justify-start'}`}>
                  {hasThumbnail ? (
                    <>
                      <div className="flex justify-center">
                        {renderThumbnail()}
                      </div>
                      <div className="flex justify-center">
                        {renderVideoPlayer()}
                      </div>
                    </>
                  ) : (
                    <div className="w-1/2 max-w-2xl">
                      {renderVideoPlayer()}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
                  <div className="sm:py-4 sm:px-2 py-2 px-2">
                    <div className="relative z-0 w-full group">
                      <strong className="block mb-2 text-gray-700">{t('TITLE')}:</strong>
                      <h6 className="text-gray-900 font-medium">{video?.title || "N/A"}</h6>
                    </div>
                  </div>
                  
                  <div className="sm:py-4 sm:px-2 py-2 px-2">
                    <div className="relative z-0 w-full group">
                      <strong className="block mb-2 text-gray-700">{t('VIDEO_TYPE')}:</strong>
                      <h6 className="text-gray-900">{getVideoTypeBadge(video?.videoType)}</h6>
                    </div>
                  </div>

                  {video?.videoType === 'youtube' && video?.videoLink && (
                    <div className="sm:py-4 sm:px-2 py-2 px-2 col-span-1 md:col-span-2 lg:col-span-2">
                      <div className="relative z-0 w-full group">
                        <strong className="block mb-2 text-gray-700">{t('YOUTUBE_LINK')}:</strong>
                        <a 
                          href={video.videoLink} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 break-words font-medium"
                        >
                          {video.videoLink}
                        </a>
                      </div>
                    </div>
                  )}

                  {video?.videoType === 'upload' && video?.videoFile && (
                    <div className="sm:py-4 sm:px-2 py-2 px-2 col-span-1 md:col-span-2 lg:col-span-2">
                      <div className="relative z-0 w-full group">
                        <strong className="block mb-2 text-gray-700">{t('VIDEO_FILE')}:</strong>
                        <a 
                          href={video.videoFile} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 break-words font-medium"
                        >
                          {video.videoFile.split('/').pop() || video.videoFile}
                        </a>
                      </div>
                    </div>
                  )}

                  {video?.videoType === 'upload' && video?.thumbnail && (
                    <div className="sm:py-4 sm:px-2 py-2 px-2 col-span-1 md:col-span-2 lg:col-span-2">
                      <div className="relative z-0 w-full group">
                        <strong className="block mb-2 text-gray-700">{t('THUMBNAIL_FILE')}:</strong>
                        <a 
                          href={video.thumbnail} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 break-words font-medium"
                        >
                          {video.thumbnail.split('/').pop() || video.thumbnail}
                        </a>
                      </div>
                    </div>
                  )}

                  <div className="sm:py-4 sm:px-2 py-2 px-2 col-span-1 md:col-span-2 lg:col-span-2">
                    <div className="relative z-0 w-full group">
                      <strong className="block mb-2 text-gray-700">{t('DESCRIPTION')}:</strong>
                      <div className="text-gray-900 whitespace-pre-wrap bg-gray-50 p-4 rounded-lg border border-gray-200">
                        {video?.description || "N/A"}
                      </div>
                    </div>
                  </div>
                  <div className="sm:py-4 sm:px-2 py-2 px-2">
                    <div className="relative z-0 w-full group">
                      <strong className="block mb-2 text-gray-700">{t('STATUS')}:</strong>
                      <h6 className="text-gray-900">{getStatusBadge(video?.status)}</h6>
                    </div>
                  </div>
                  
                  <div className="sm:py-4 sm:px-2 py-2 px-2">
                    <div className="relative z-0 w-full group">
                      <strong className="block mb-2 text-gray-700">{t('CREATED_DATE')}:</strong>
                      <h6 className="text-gray-900">
                        {video?.createdAt ? dayjs(video.createdAt).format("MM-DD-YYYY hh:mm A") : "N/A"}
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-end p-6 border-t border-solid border-slate-200 rounded-b">
        <button 
          className="text-black bg-[#E1E1E1] font-normal px-12 py-2.5 text-sm outline-none focus:outline-none rounded mr-6  ease-linear duration-150 hover:bg-gray-200 transition-colors" 
          type="button" 
          onClick={() => navigate("/video-management")}
        >
          {t("O_BACK")}
        </button>
      </div>
    </>
  );
};

export default VideoView;



// import React, { useContext, useEffect } from "react";
// import { useTranslation } from "react-i18next";
// import dayjs from "dayjs";
// import { startCase } from "lodash";
// import AuthContext from "context/AuthContext";
// import { useLocation, useNavigate } from "react-router-dom";

// const VideoView = () => {
//   const { t } = useTranslation();
//   const location = useLocation();
//   const { updatePageName } = useContext(AuthContext);
//   const navigate = useNavigate();
  
//   useEffect(() => {
//     updatePageName(t("VIEW_VIDEO"));
//   }, []);

//   const video = location?.state;

//   const getYouTubeEmbedUrl = (url) => {
//     if (!url) return null;
    
//     // Extract video ID from various YouTube URL formats
//     const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
//     const match = url.match(regExp);
//     const videoId = (match && match[7].length === 11) ? match[7] : null;
    
//     return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
//   };

//   const renderVideoPlayer = () => {
//     if (video?.videoType === 'youtube' && video?.videoLink) {
//       const embedUrl = getYouTubeEmbedUrl(video.videoLink);
//       if (embedUrl) {
//         return (
//           <div className="w-full max-w-2xl mx-auto">
//             <iframe
//               src={embedUrl}
//               title={video.title}
//               allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
//               allowFullScreen
//               className="w-full rounded-lg"
//             ></iframe>
//           </div>
//         );
//       }
//       return <div className="text-red-500">Invalid YouTube URL</div>;
//     } else if (video?.videoType === 'upload' && video?.videoFile) {
//       return (
//         <div className="w-full max-w-2xl mx-auto">
//           <video controls className="w-full rounded-lg">
//             <source src={video.videoFile} type="video/mp4" />
//             Your browser does not support the video tag.
//           </video>
//         </div>
//       );
//     }
//     return <div className="text-gray-500">No video available</div>;
//   };

//   const renderThumbnail = () => {
//     if (video?.videoType === 'upload' && video?.thumbnail) {
//       return (
//         <div className="w-full max-w-2xl mx-auto">
//           <div className="bg-gray-50 p-4 rounded-lg">
//             <h3 className="text-lg font-semibold mb-3 text-gray-800">{t('THUMBNAIL')}</h3>
//             <div className="flex justify-center">
//               <img 
//                 src={video.thumbnail} 
//                 alt={`${video.title} thumbnail`}
//                 className="max-w-full rounded-lg shadow-md object-contain"
//               />
//             </div>
//           </div>
//         </div>
//       );
//     }
//     return null;
//   };

//   const getVideoTypeBadge = (type) => {
//     const colors = {
//       youtube: 'bg-red-100 text-red-800',
//       upload: 'bg-blue-100 text-blue-800'
//     };
//     return (
//       <span className={`px-3 py-1 rounded-full text-sm ${colors[type]}`}>
//         {type === 'youtube' ? 'YouTube' : 'Uploaded Video'}
//       </span>
//     );
//   };

//   const getStatusBadge = (status) => {
//     const colors = {
//       active: 'bg-green-100 text-green-800',
//       inactive: 'bg-red-100 text-red-800'
//     };
//     return (
//       <span className={`px-3 py-1 rounded-full text-sm ${colors[status]}`}>
//         {startCase(status)}
//       </span>
//     );
//   };

//   return (
//     <>
//       <div>
//         <div className="bg-[#F9F9F9]">
//           <div className="px-3 py-4">
//             <div className="bg-white border border-[#E9EDF9] rounded-lg">
//               <div className=" border-b-[#E3E3E3] grid 2xl:grid-cols-3 xl:grid-cols-2 lg:grid lg:grid-cols-1 gap-2 px-4 ">
//                 <div className="col-span-2 flex flex-wrap  items-center" />
//               </div>
//               <div className="relative p-6 flex-auto">
//                 {/* Video Player */}
//                 <div className="mb-8 flex gap-2 justify-center">
//                   {renderThumbnail()}
//                   {renderVideoPlayer()}
//                 </div>
//                 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
//                   <div className="sm:py-4 sm:px-2 py-2 px-2">
//                     <div className="relative z-0 w-full group">
//                       <strong className="block mb-2 text-gray-700">{t('TITLE')}:</strong>
//                       <h6 className="text-gray-900 font-medium">{video?.title || "N/A"}</h6>
//                     </div>
//                   </div>
                  
//                   <div className="sm:py-4 sm:px-2 py-2 px-2">
//                     <div className="relative z-0 w-full group">
//                       <strong className="block mb-2 text-gray-700">{t('VIDEO_TYPE')}:</strong>
//                       <h6 className="text-gray-900">{getVideoTypeBadge(video?.videoType)}</h6>
//                     </div>
//                   </div>

//                   {video?.videoType === 'youtube' && video?.videoLink && (
//                     <div className="sm:py-4 sm:px-2 py-2 px-2 col-span-1 md:col-span-2 lg:col-span-2">
//                       <div className="relative z-0 w-full group">
//                         <strong className="block mb-2 text-gray-700">{t('YOUTUBE_LINK')}:</strong>
//                         <a 
//                           href={video.videoLink} 
//                           target="_blank" 
//                           rel="noopener noreferrer"
//                           className="text-blue-600 hover:text-blue-800 break-words font-medium"
//                         >
//                           {video.videoLink}
//                         </a>
//                       </div>
//                     </div>
//                   )}

//                   {video?.videoType === 'upload' && video?.videoFile && (
//                     <div className="sm:py-4 sm:px-2 py-2 px-2 col-span-1 md:col-span-2 lg:col-span-2">
//                       <div className="relative z-0 w-full group">
//                         <strong className="block mb-2 text-gray-700">{t('VIDEO_FILE')}:</strong>
//                         <a 
//                           href={video.videoFile} 
//                           target="_blank" 
//                           rel="noopener noreferrer"
//                           className="text-blue-600 hover:text-blue-800 break-words font-medium"
//                         >
//                           {video.videoFile.split('/').pop() || video.videoFile}
//                         </a>
//                       </div>
//                     </div>
//                   )}

//                   {video?.videoType === 'upload' && video?.thumbnail && (
//                     <div className="sm:py-4 sm:px-2 py-2 px-2 col-span-1 md:col-span-2 lg:col-span-2">
//                       <div className="relative z-0 w-full group">
//                         <strong className="block mb-2 text-gray-700">{t('THUMBNAIL_FILE')}:</strong>
//                         <a 
//                           href={video.thumbnail} 
//                           target="_blank" 
//                           rel="noopener noreferrer"
//                           className="text-blue-600 hover:text-blue-800 break-words font-medium"
//                         >
//                           {video.thumbnail.split('/').pop() || video.thumbnail}
//                         </a>
//                       </div>
//                     </div>
//                   )}

//                   <div className="sm:py-4 sm:px-2 py-2 px-2 col-span-1 md:col-span-2 lg:col-span-2">
//                     <div className="relative z-0 w-full group">
//                       <strong className="block mb-2 text-gray-700">{t('DESCRIPTION')}:</strong>
//                       <div className="text-gray-900 whitespace-pre-wrap bg-gray-50 p-4 rounded-lg border border-gray-200">
//                         {video?.description || "N/A"}
//                       </div>
//                     </div>
//                   </div>
//                   <div className="sm:py-4 sm:px-2 py-2 px-2">
//                     <div className="relative z-0 w-full group">
//                       <strong className="block mb-2 text-gray-700">{t('STATUS')}:</strong>
//                       <h6 className="text-gray-900">{getStatusBadge(video?.status)}</h6>
//                     </div>
//                   </div>
                  
//                   <div className="sm:py-4 sm:px-2 py-2 px-2">
//                     <div className="relative z-0 w-full group">
//                       <strong className="block mb-2 text-gray-700">{t('CREATED_DATE')}:</strong>
//                       <h6 className="text-gray-900">
//                         {video?.createdAt ? dayjs(video.createdAt).format("MM-DD-YYYY hh:mm A") : "N/A"}
//                       </h6>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//       <div className="flex items-center justify-end p-6 border-t border-solid border-slate-200 rounded-b">
//         <button 
//           className="text-black bg-[#E1E1E1] font-normal px-12 py-2.5 text-sm outline-none focus:outline-none rounded mr-6  ease-linear duration-150 hover:bg-gray-200 transition-colors" 
//           type="button" 
//           onClick={() => navigate("/video-management")}
//         >
//           {t("O_BACK")}
//         </button>
//       </div>
//     </>
//   );
// };

// export default VideoView;